import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LongTextarea } from "@/components/LongText";
import { Mail, Send, Power, Save } from "lucide-react";

const TIPI = [
  ["conferma_attiva", "testo_conferma", "conferma", "Conferma appuntamento (giorno prima)", "Al cliente dell'appuntamento. Segnaposto: {nome} {ora} {indirizzo} {agente}."],
  ["rapporto_attivo", "testo_rapporto", "rapporto", "Rapporto settimanale al proprietario (lunedì)", "Al proprietario di ogni immobile attivo. Segnaposto: {nome} {indirizzo} {riepilogo}."],
  ["recensione_attiva", "testo_recensione", "recensione", "Richiesta recensione (2 giorni dopo il rogito)", "Al cliente dopo il rogito. Richiede il link Google qui sotto."],
];

export default function ComunicazioniClienti() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");

  const load = () => api.get("/settings/fase5").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    const val = !cfg[key];
    setCfg({ ...cfg, [key]: val });
    try { const r = await api.put("/settings/fase5", { [key]: val }); setCfg(r.data); toast.success(val ? "Comunicazione ATTIVATA" : "Comunicazione spenta"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); load(); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/fase5", {
        google_review_link: cfg.google_review_link || "",
        testo_conferma: cfg.testo_conferma, testo_rapporto: cfg.testo_rapporto, testo_recensione: cfg.testo_recensione,
      });
      setCfg(r.data); toast.success("Testi salvati");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };
  const inviaProva = async (tipo) => {
    setTesting(tipo);
    try { const r = await api.post("/settings/fase5/test", { tipo }); if (r.data.bloccato) toast.warning(`Bloccato (anteprima): nessuna email a ${r.data.sent_to}`); else toast.success(`Email di prova inviata a ${r.data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); } finally { setTesting(""); }
  };

  if (!cfg) return <div className="skeleton h-40 rounded-2xl" />;

  return (
    <div className="card-immo p-4" data-testid="fase5-settings">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2"><Mail size={14} /> Comunicazioni ai clienti (solo Soci)</div>
      <p className="text-[11px] text-[var(--muted)] mb-4">Email automatiche inviate ai clienti. Mittente visualizzato "Immobilart", le risposte arrivano a segreteria@immobilart.info. Valgono solo dal momento in cui le accendi. Mai richieste di documenti; ogni email ha il link per disiscriversi.</p>

      {TIPI.map(([atk, txtk, tipo, titolo, hint]) => (
        <div key={tipo} className="rounded-xl border border-[var(--border)] p-3 mb-3" data-testid={`fase5-blocco-${tipo}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="font-semibold text-sm">{titolo}</div>
            <button data-testid={`fase5-toggle-${tipo}`} onClick={() => toggle(atk)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${cfg[atk] ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600"}`}>
              <Power size={13} /> {cfg[atk] ? "ATTIVA" : "SPENTA"}
            </button>
          </div>
          <p className="text-[11px] text-[var(--muted)] mb-2">{hint}</p>
          <LongTextarea data-testid={`fase5-testo-${tipo}`} rows={3} value={cfg[txtk] || ""} onChange={(e) => setCfg({ ...cfg, [txtk]: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm mb-2" />
          {tipo === "recensione" && (
            <input data-testid="fase5-review-link" type="url" placeholder="https://g.page/... (link recensioni Google)" value={cfg.google_review_link || ""}
              onChange={(e) => setCfg({ ...cfg, google_review_link: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm mb-2" />
          )}
          <button data-testid={`fase5-test-${tipo}`} onClick={() => inviaProva(tipo)} disabled={testing === tipo}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium disabled:opacity-50">
            <Send size={13} /> {testing === tipo ? "Invio..." : "Invia email di prova a me"}
          </button>
        </div>
      ))}

      <button data-testid="fase5-save" onClick={save} disabled={saving}
        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-50"><Save size={15} /> {saving ? "Salvo..." : "Salva testi e link"}</button>
    </div>
  );
}

export function ConfermeDaChiamare({ personalizza }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/dashboard/conferme-appuntamenti").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const conferma = async (id) => { try { await api.post(`/appuntamenti/${id}/conferma-manuale`); toast.success("Appuntamento confermato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const mostraScript = (s) => toast.message(personalizza ? personalizza(s) : s, { duration: 9000 });
  if (!list.length) return null;
  return (
    <div className="mb-6" data-testid="conferme-da-chiamare">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2"><Mail size={14} /> Appuntamenti di domani da confermare (chiama il cliente)</div>
      <div className="space-y-2">
        {list.map((a) => (
          <div key={a.id} data-testid={`conferma-${a.id}`} className="card-immo p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div><div className="text-sm font-semibold">{a.cliente} · {a.ora}</div><div className="text-xs text-[var(--muted)]">con {a.agente || "—"} · non ha ancora confermato</div></div>
              <div className="flex gap-1.5">
                <button data-testid={`conferma-script-${a.id}`} onClick={() => mostraScript(a.script)} className="px-2.5 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium">Script chiamata</button>
                <button data-testid={`conferma-ok-${a.id}`} onClick={() => conferma(a.id)} className="px-2.5 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold">Segna confermato</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
